import { CompanyResponse } from '@business/entities/companies';
import { MeetingType } from './../../../modules/CalendarPage/AddSchedularModal/AddSchedularModal';
import { Session } from './../../../modules/auth/session/session';
import { GCCountry, GCCountryData, GCState } from '@business/constants/common/states';
import { EmailType } from '@business/constants/emailtype';
import { VideoPlatform } from '@business/constants/videoPlatform';
import { CompactUserResponse, UserResponse, UserRole, VeryCompactUserResponse } from '@business/entities/account';
import { SyncFreqency } from '@business/entities/common/SyncFreqency';
import { CompactContactResponse, ContactResponse } from '@business/entities/contact';
import { IntegrationType } from '@business/entities/integration';
import { OrgAddressType } from '@business/entities/organization';
import { SettingsResponse } from '@business/entities/settings';
import { TeamResponse } from '@business/entities/team';
import { FormSelectItem } from '@core/components/form';
import { SettingsConfig, AppConfig } from '@core/utils/config';
import { R } from '@core/utils/r';
import { AvatarGroupItem } from './../../../modules/common/AvatarGroup/AvatarGroup';
import { Subject } from 'rxjs';
import { SettingFieldResponse } from '@business/entities/settingFields';
import { TabsMapping } from '@modules/common';

// map<U>(callbackfn: (value: T, index: number, array: T[]) => U, thisArg?: any): U[]
export type SelectCallbackFn<I, T> = (item: I, index: number) => T;
export function selectItems<I, V extends string | boolean | number | any = any>(
  items: I[],
  label: keyof I | SelectCallbackFn<I, string>,
  value?: keyof I | SelectCallbackFn<I, V>,
  sort?: boolean,
  data?: keyof I | SelectCallbackFn<I, any>,
): FormSelectItem<V>[] {
  const listItem = items.map((item, index) => ({
    label: R.isFunction(label) ? label(item, index) : R.get(item, label),
    value: value ? (R.isFunction(value) ? value(item, index) : R.get(item, value)) : item,
    data: data ? (R.isFunction(data) ? data(item, index) : R.get(item, data)) : item,
  }));

  if (sort) {
    return R.sortBy(listItem, i => R.toString(i.label).toLowerCase());
  }
  return listItem;
}

export function selectGroupedItems<G, I, V extends string | boolean | number = any>(
  groups: G[],
  groupLabel: keyof G | SelectCallbackFn<G, string>,
  itemsAccessor: SelectCallbackFn<G, I[]>,
  label: keyof I | SelectCallbackFn<I, string>,
  value?: keyof I | SelectCallbackFn<I, V>,
  sort?: boolean,
): FormSelectItem<V>[] {
  const listItems = groups.map((group, index) => {
    const gItems: I[] = R.isFunction(itemsAccessor) ? itemsAccessor(group, index) : R.get(group, itemsAccessor);

    return {
      value: R.isFunction(groupLabel) ? groupLabel(group, index) : R.get(group, groupLabel),
      label: R.isFunction(groupLabel) ? groupLabel(group, index) : R.get(group, groupLabel),
      options: selectItems(gItems, label, value, sort),
    };
  });

  if (sort) {
    return R.sortBy(listItems, i => i.label);
  }
  return listItems;
}

export function getFullName(
  user: UserResponse | CompactUserResponse | CompactContactResponse | ContactResponse | VeryCompactUserResponse,
  options?: { meId?: string; affiliate?: boolean },
) {
  if (!user) {
    return 'Deleted User';
  }
  if (user.id === options?.meId) {
    return R.join(R.compact(['Me', '']), ' ');
  }
  if ('name' in user) {
    return user.name;
  }
  if (options && options.affiliate && R.get(user, 'role') == UserRole.affiliate) {
    return R.join(R.compact([user.firstName, user.lastName, '(Affiliate)']), ' ');
  }
  return R.join(R.compact([user.firstName, user.lastName]), ' ');
}

export function getShortName(user: UserResponse | CompactUserResponse | CompactContactResponse | ContactResponse) {
  return R.join(R.compact([user.firstName[0], user.lastName[0]]), '').toUpperCase();
}

export function getShortNameFromString(string: string) {
  const shortName = R.join(R.compact(R.split(string, ' ').map(item => R.get(item, '0') || '')), '').toUpperCase();
  if (shortName.length > 1) {
    return shortName.replace(/[^A-Z]/g, '');
  }
  return R.join(R.compact(R.split(string, '').map(item => R.get(item, '0') || '')), '').toUpperCase();
}

export function getEmailType() {
  return [EmailType.work, EmailType.home, EmailType.other];
}

export function getPhoneType() {
  return [EmailType.work, EmailType.home, EmailType.mobile, EmailType.other];
}

export function getEmailTypeTitle(type: EmailType) {
  return R.upperFirst(type.toString());
}

export function getCountries() {
  return GCCountryData;
}

export function getCountriesItems() {
  return R.sortBy(
    R.map(GCCountryData, item => ({ label: item.name, value: item.code })),
    item => item.label,
  );
}

export function findCountry(search: string) {
  return R.find(GCCountryData, i => R.lowerCase(i.name) === R.lowerCase(search) || R.lowerCase(i.code) === R.lowerCase(search));
}

export function findState(country: GCCountry, search: string) {
  return (
    R.find(country.states, i => R.lowerCase(i.name) === R.lowerCase(search) || R.lowerCase(i.stateCode) === R.lowerCase(search))?.stateCode || search
  );
}

export function getStates(country: string): GCState[] {
  const c = R.find(GCCountryData, item => item.code === country);
  if (c) return c.states;
  return [];
}

export function getStatesItems(country: string) {
  const c = R.find(GCCountryData, item => item.code === country);
  if (c)
    return R.sortBy(
      c.states.map(item => ({ label: item.name, value: item.stateCode })),
      s => s.label,
    );
  return [];
}

export function isImage(extension: string) {
  extension = R.lowerCase(extension);
  if (extension === 'jpeg' || extension === 'jpg' || extension === 'png' || extension === 'gif' || extension === 'bmp') {
    return true;
  }
  return false;
}

export function copyToClipboard(text: string) {
  const el = document.createElement('textarea');
  el.value = text;
  el.setAttribute('readonly', '');
  el.style.position = 'absolute';
  el.style.left = '-9999px';
  document.body.appendChild(el);
  el.select();
  document.execCommand('copy');
  document.body.removeChild(el);
}

export function stripTags(value: string) {
  return R.isString(value) ? value.replace(/(<([^>]+)>)/gi, '') : value;
}

export function stripHtml(html: string) {
  const temp = document.createElement('div');
  temp.innerHTML = html;
  return temp.textContent || temp.innerText || '';
}

export function toFormData(model: any, form: FormData | null = null, namespace = ''): FormData {
  const formData = form || new FormData();
  for (const propertyName in model) {
    if (!model.hasOwnProperty(propertyName) || model[propertyName] == undefined) continue;
    const formKey = namespace ? `${namespace}[${propertyName}]` : propertyName;
    if (model[propertyName] instanceof File) {
      formData.append(formKey, model[propertyName]);
    } else if (model[propertyName] instanceof Array) {
      model[propertyName].forEach((element: any, index: number) => {
        if (typeof element != 'object') formData.append(`${formKey}[]`, element);
        else if (element instanceof File) formData.append(`${formKey}[]`, element);
        else {
          const tempFormKey = `${formKey}[${index}]`;
          toFormData(element, formData, tempFormKey);
        }
      });
    } else if (typeof model[propertyName] === 'object' && !(model[propertyName] instanceof File)) {
      toFormData(model[propertyName], formData, formKey);
    } else {
      formData.append(formKey, model[propertyName].toString());
    }
  }
  return formData;
}

export function getDays() {
  return [
    { label: 'Monday', value: 1 },
    { label: 'Tuesday', value: 2 },
    { label: 'Wednesday', value: 3 },
    { label: 'Thursday', value: 4 },
    { label: 'Friday', value: 5 },
    { label: 'Saturday', value: 6 },
    { label: 'Sunday', value: 7 },
  ];
}

export function getShortDays() {
  return [
    { label: 'mon', value: 1 },
    { label: 'tue', value: 2 },
    { label: 'wed', value: 3 },
    { label: 'thu', value: 4 },
    { label: 'fri', value: 5 },
    { label: 'sat', value: 6 },
    { label: 'sun', value: 7 },
  ];
}

export function getDayByValue(value: number) {
  switch (value) {
    case 1:
      return 'Mon';
    case 2:
      return 'Tue';
    case 3:
      return 'Wed';
    case 4:
      return 'Thur';
    case 5:
      return 'Fri';
    case 6:
      return 'Sat';
    case 7:
      return 'Sun';
    default:
      return 'Unknown';
  }
}

export function getWeekendingTime() {
  return [
    { label: '00:00', value: '00:00' },
    { label: '01:00', value: '01:00' },
    { label: '02:00', value: '02:00' },
    { label: '03:00', value: '03:00' },
    { label: '04:00', value: '04:00' },
    { label: '05:00', value: '05:00' },
    { label: '06:00', value: '06:00' },
    { label: '07:00', value: '07:00' },
    { label: '08:00', value: '08:00' },
    { label: '09:00', value: '09:00' },
    { label: '10:00', value: '10:00' },
    { label: '11:00', value: '11:00' },
    { label: '12:00', value: '12:00' },
    { label: '13:00', value: '13:00' },
    { label: '14:00', value: '14:00' },
    { label: '15:00', value: '15:00' },
    { label: '16:00', value: '16:00' },
    { label: '17:00', value: '17:00' },
    { label: '18:00', value: '18:00' },
    { label: '19:00', value: '19:00' },
    { label: '20:00', value: '20:00' },
    { label: '21:00', value: '21:00' },
    { label: '22:00', value: '22:00' },
    { label: '23:00', value: '23:00' },
    // { label: 'End of Day', value: '23:59' },
  ];
}

export function readMore(value: string, limit = 20) {
  return R.isString(value) && value.length > limit ? stripTags(value).substring(0, limit) + '...' : value;
}

export function getMonthDays() {
  const days = [];
  for (let i = 1; i <= 29; i++) {
    days.push({ label: 'Day ' + i, value: i });
  }
  //   days.push({ label: 'End of Month', value: 99 });
  return days;
}

export function getAvatarItems(ids: string[], users: CompactUserResponse[], teams: TeamResponse[]): AvatarGroupItem[] {
  return R.pipe(
    ids,
    R.ops.map(id => {
      if (id.includes('user')) {
        return R.find(users, t => t.id.toString() === id.replace('user-', ''));
      } else {
        return R.find(teams, t => t.id.toString() === id.replace('team-', ''));
      }
    }),
    R.ops.compact,
    R.ops.map(item => {
      return {
        id: item.id,
        title: R.get(item, 'name') || getFullName(item as CompactUserResponse),
      };
    }),
  );
}

export function isIntegrationEnabled(type: IntegrationType, inSettings?: SettingsResponse) {
  const settings = inSettings || SettingsConfig.current();
  if (settings) {
    return settings.integrations.filter(i => i.type === type).length > 0;
  }
  return false;
}

export function getSyncFrequencyLabel(frequency: SyncFreqency) {
  switch (frequency) {
    case SyncFreqency.hourly:
      return 'Hourly';
    case SyncFreqency.daily:
      return 'Daily';
    case SyncFreqency.weekly:
      return 'Weekly';
    case SyncFreqency.monthly:
      return 'Monthly';
  }
}

export function getAddressTypeLabel(type: OrgAddressType) {
  switch (type) {
    case OrgAddressType.billing:
      return 'Billing';
    case OrgAddressType.shipping:
      return 'Shipping';
    case OrgAddressType.both:
      return 'Billing, Shipping';
  }
}

export function getVideoPlatform(url: string) {
  if (url.indexOf('https://youtube.com') > -1) {
    return VideoPlatform.youtube;
  } else if (url.indexOf('https://vimeo.com') > -1) {
    return VideoPlatform.vimeo;
  } else if (url.indexOf('https://www.loom.com') > -1 || url.indexOf('https://loom.com') > -1) {
    return VideoPlatform.loom;
  } else {
    return VideoPlatform.youtube;
  }
}

export function getEmbedUrl(url: string, videoPlatform: VideoPlatform) {
  if (videoPlatform === VideoPlatform.loom && url.indexOf('/share/') > -1) {
    const urlArray = url.split('/share/');
    const loomVideoId = urlArray[1] ?? '';
    return 'https://www.loom.com/embed/' + loomVideoId;
  } else {
    return url;
  }
}
export function isValidUrl(str: string) {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i',
  ); // fragment locator
  return !!pattern.test(str);
}

export function YesNoOptions(labels?: { yes?: string; no?: string }) {
  return [
    { label: labels?.yes || 'Yes', value: true },
    { label: labels?.no || 'No', value: false },
  ];
}

export function chooseImage(options?: { multiple?: boolean }) {
  const obs$ = new Subject<FileList>();

  const input = document.createElement('input');
  input.type = 'file';
  input.accept = 'image/*';
  input.multiple = options?.multiple || false;
  input.onchange = e => {
    const files = (e.target as HTMLInputElement).files;
    if (files && files.length > 0) {
      obs$.next(files);
      obs$.complete();
    }
  };
  input.click();

  return obs$;
}

export function move(stepsIds: string[], oldIndex: number, newIndex: number) {
  throw new Error('Function not implemented.');
}

export function isJson(item: any) {
  let value = typeof item !== 'string' ? JSON.stringify(item) : item;
  try {
    value = JSON.parse(value);
  } catch (e) {
    return false;
  }

  return typeof value === 'object' && value !== null;
}

export function isTenants(t: string[]) {
  return t.includes(AppConfig.current().appId || '') || t.includes(R.toString(AppConfig.current().appId || ''));
}

export function isStudio98() {
  return isTenants(['1616359454']);
}

export function isWiseContentDisabled(field: SettingFieldResponse, value: any) {
  if (!isTenants(['1655231005'])) return false;
  const user = Session.user;
  if (!user) return false;
  if (user.role === UserRole.admin) return false;
  const continent = {
    63: 'All Continents+EUS',
    64: 'Africa',
    65: 'Australia, New Zealand, Oceania',
    66: 'Asia',
    67: 'Canada',
    68: 'Central Eastern Europe',
    69: 'Commonwealth of Independent States',
    70: 'Flag Land Base',
    71: 'Europe w/o',
    72: 'Europe+CEE+ITA+CIS(limited)',
    73: 'Eastern United States',
    74: 'Eastern United States + Flag Land Base',
    75: 'Italy',
    76: 'Latin America',
    77: 'United Kingdom',
    78: 'Western United States',
    79: 'Taiwan',
    80: 'All US, EUS+WUS+FLB Combined',
    81: 'EUS+WUS+FLB+CAN Combined',
    82: 'All Continents MINUS EUS',
    83: 'All Eastern Hemisphere Conts',
    84: 'All Western Hemisphere Conts',
  };
  const numValue = R.toNumber(value);
  if (field.id == '25' && numValue > 0 && R.get(continent, numValue)) {
    if (user.teamsNames.filter(t => t === R.get(continent, numValue)).length > 0) {
      return false;
    }
    return true;
  }
  return false;
}

export function isValidImage(name: string | null) {
  if (!name) return false;
  const ext = name.split('.').pop();
  const extension = R.lowerCase(ext);
  if (extension === 'jpeg' || extension === 'jpg' || extension === 'png' || extension === 'gif' || extension === 'bmp') {
    return true;
  }
  return false;
}

export function isHive() {
  return isTenants(['1675191225', '0000000000', '1699449111']);
}
export function isAaron() {
  return isTenants(['1703850751']);
}
export function isCanadaFirst() {
  return isTenants(['1715030651']);
}
export function isYesa() {
  return isTenants(['1673302957']);
}

export function canCreateCsvTable() {
  return isTenants(['1673302957']);
}

export function getMeetingTypeLabel(type: MeetingType) {
  switch (type) {
    case MeetingType.collective:
      return 'All Available';
    case MeetingType.roundRobin:
      return 'Round Robin';
    case MeetingType.nextAvailable:
      return 'Next Available';
    default:
      return 'Unknown';
  }
}

export function isProjectTimeTrackingEnabled(settings: SettingsResponse | null) {
  return settings && settings.project.isProjectTimeTracking;
}

export function isSupportTimeTrackingEnabled(settings: SettingsResponse | null) {
  return settings && settings.support.isSupportTimeTracking;
}

export function isTmTimeTrackingEnabled(settings: SettingsResponse | null) {
  return settings && settings.tm.isTimeTracking;
}

export function isTmAutomaticTrackingEnabled(settings: SettingsResponse | null) {
  return settings && settings.tm.automaticTrackTime && isTmTimeTrackingEnabled(settings);
}

export function isSupportAutomaticTrackingEnabled(settings: SettingsResponse | null) {
  return settings && settings.support.supportAutomaticTrackTime && isSupportTimeTrackingEnabled(settings);
}

export function isProjectAutomaticTrackingEnabled(settings: SettingsResponse | null) {
  return settings && settings.project.projectAutomaticTrackTime && isProjectTimeTrackingEnabled(settings);
}

export function getInitials(name: string) {
  return R.join(R.compact(R.split(name, ' ').map(item => R.get(item, '0') || '')), '').toUpperCase();
}

export function sortByTitle(array: TabsMapping[]): TabsMapping[] {
  return array.sort((a, b) => {
    const titleA = a.title;
    const titleB = b.title;
    return titleA.localeCompare(titleB);
  });
}

export function getDefaultPagePath() {
  return '/home';
}

export function getCountryCode(company: CompanyResponse) {
  return company.countryCode || 'us';
}

export function isOnePageProposalEnabled(inSettings: SettingsResponse, isStage = false) {
  if (isStage) return true;
  if (!inSettings) return false;
  const proposalSettings = inSettings.proposalSettings;
  if (!proposalSettings) return false;
  return proposalSettings?.enableOnePageProposal || isStage;
}

export function safeBtoa(str: string): string {
  return btoa(String.fromCharCode(...Array.from(new TextEncoder().encode(str))));
}

export function safeAtob(str: string): string {
  return new TextDecoder().decode(Uint8Array.from(atob(str), c => c.charCodeAt(0)));
}

export function applyCourseChanges(settings: SettingsResponse | null) {
  return settings && settings.applyCourseChangesToEnrollers;
}

export function applyCourseChangesToAll(settings: SettingsResponse | null) {
  return settings && settings.applyCourseChangesToCompleted;
}

export function isGCInternalSchedularEnabled(settings: SettingsResponse | null) {
  const inSettings = settings || SettingsConfig.current();
  return R.get(inSettings, 'schedular.isGCSchedularEnabled', false);
}

export function emailShortCodes() {
  return {
    'h:Your Company': 'h:Your Company',
    company_name: 'Company Name',
    brand_name: 'Brand Name',
    user_name: 'User Name',
    user_email: 'User Email',
    // user_phone: 'User Phone',
    company_address: 'Address',
    company_city: 'City',
    company_state: 'State',
    company_country: 'Country',
    company_zip: 'Zip',
    // '|1': '|',
    'h:Your Client': 'h:Your Client',
    org_name: 'Organization Name',
    contact_first_name: 'Contact First Name',
    contact_last_name: 'Contact Last Name',
    contact_name: 'Contact Name',
    contact_email: 'Contact Email',
    contact_address: 'Address',
    contact_address2: 'Address 2',
    contact_city: 'City',
    contact_state: 'State',
    contact_zip: 'Zip Code',
    contact_country: 'Country',
    contact_website: 'Website',
  };
}
